import type { I18n } from '@backmarket/nuxt-module-i18n/types'

const DATE_RANGE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}

// Custom date range formatter for SalesInsights on the Home page
// It formats the date range as "Nov 1-8, 2025" if the start and end dates are in the same month and year
// Otherwise, it formats the data range including the other month like "Nov 30 - Dec 4, 2025"
export function formatDateRangeForInsightBlock(
  startDate: string,
  endDate: string,
  i18n: I18n,
): string {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const startMonth = start.getMonth()
  const startYear = start.getFullYear()
  const endMonth = end.getMonth()
  const endYear = end.getFullYear()

  // If same month and year, format as "Nov 1-8, 25"
  if (startMonth === endMonth && startYear === endYear) {
    const formatter = new Intl.DateTimeFormat(i18n.currentLocale, {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
    })

    const startParts = formatter.formatToParts(start)
    const endParts = formatter.formatToParts(end)

    const month = startParts.find((part) => part.type === 'month')?.value ?? ''
    const startDay = startParts.find((part) => part.type === 'day')?.value ?? ''
    const endDay = endParts.find((part) => part.type === 'day')?.value ?? ''
    const year = startParts.find((part) => part.type === 'year')?.value ?? ''

    return `${month} ${startDay} - ${endDay}, ’${year}`
  }

  // If same year but different months, format as "Nov 30 - Dec 4, 2025"
  if (startYear === endYear && startMonth !== endMonth) {
    const formatter = new Intl.DateTimeFormat(i18n.currentLocale, {
      month: 'short',
      day: 'numeric',
    })

    const startParts = formatter.formatToParts(start)
    const endParts = formatter.formatToParts(end)

    const startMonthName =
      startParts.find((part) => part.type === 'month')?.value ?? ''
    const startDay = startParts.find((part) => part.type === 'day')?.value ?? ''
    const endMonthName =
      endParts.find((part) => part.type === 'month')?.value ?? ''
    const endDay = endParts.find((part) => part.type === 'day')?.value ?? ''

    const yearFormatter = new Intl.DateTimeFormat(i18n.currentLocale, {
      year: '2-digit',
    })
    const year = yearFormatter.format(start)

    return `${startMonthName} ${startDay} - ${endMonthName} ${endDay}, ’${year}`
  }

  // If different years, format both dates separately
  return `${i18n.date(start, DATE_RANGE_FORMAT)} - ${i18n.date(end, DATE_RANGE_FORMAT)}`
}
